(ns frontend.view.reports.custom.alj-2.alj-components
  (:require
   [ui.util :as uu]
   [cljsjs.html2canvas]
   [clojure.set :as set]
   [common.entity :as entity]
   [common.localization :as loc]
   [frontend.db :as app-db]
   [frontend.exports.excel :as excel]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.ui.component.chart.view :as fu-chart]
   [frontend.ui.component.date-range-picker.view :refer
    [date-range-dropdown
     date-range-picker
     this-quarter-to-date]]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.reports.custom.alj :as alj]
   [frontend.view.reports.custom.alj-2.style :as style]
   [frontend.view.reports.custom.alj-2.utils :as alj-utils]
   [potpuri.core :as p]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.card.view :as card]
   [ui.components.chart.view :as chart]))

(defn status-checkbox
  [date-atom date-field]
  [:label
   {:class "flex flex-col"}
   (loc/tr date-field)
   [:input
    {:checked   (boolean (some #{date-field} (:in-use @date-atom)))
     :on-change (fn [_]
                  (if (some #{date-field} (:in-use @date-atom))
                    (swap! date-atom assoc :in-use (set/difference (:in-use @date-atom) #{date-field}))
                    (swap! date-atom assoc :in-use (set/union (:in-use @date-atom) #{date-field})))
                  (deref date-atom))
     :type      "checkbox"}]]
  )
(defn date-picker-row
  [date-state-atom kw label]
  (let [date-atom (r/cursor date-state-atom [kw])]
    (fn [date-state-atom kw label]
      (into
       [:div
        {:class "flex items-center gap-2 flex-wrap"}
        (when date-state-atom
          [date-range-dropdown
           {:date-state-atom  date-atom
            :label            label
            :on-change        (fn [] (logging/unimplemented "refresh data"))
            :static-ranges-kw :alj}])]
       (->> (:applied-kws @date-atom)
            (map (fn [field-kw]
                   [status-checkbox date-atom field-kw])))))))

(defn render-element-to-clipboard
  [element]
  (.then
   (js/html2canvas element)
   (fn [canvas-element]
     (.toBlob
      canvas-element
      (fn [blob]
        (.write
         js/navigator.clipboard
         #js [(js/ClipboardItem. #js {"image/png" blob})]))))))

(defn alj-card
  [{:keys [view view-name total-deals total-amount report-data
           footer-content date-state-atom date-range-label chart-config]}]
  (let [card-container-ref-state (r/atom nil)]
    (fn [{:keys [view view-name total-deals total-amount report-data
                 footer-content date-state-atom date-range-label chart-config]}]
      (let [collapsed? (:collapsed? @date-state-atom)]
        [card/card
         [card/header
          [:span
           {:class "pr-2 font-semibold inline-block"}
           view-name
           " "
           (when total-deals
             [:span
              {:class "text-xs text-base-light-text"}
              (str "(" total-deals ")")])
           (when total-amount [:span
                               {:class "text-xs text-base-light-text"}
                               (str " ($" total-amount " BN)")])]
          [:div
           {:class "flex justify-end"}
           (fu/tooltip (loc/tr :copy-as-image)
                       [:i.fal.fa-image.mr-3.cursor-pointer
                        {:on-click (fn [_]
                                     (if chart-config
                                       (fu-chart/to-clipboard-image (assoc chart-config
                                                                      :title
                                                                      {:text view-name})))
                                     (render-element-to-clipboard @card-container-ref-state))}])
           (fu/tooltip (loc/tr :copy-data-as-text)
                       [:i.fal.fa-copy.mr-3.cursor-pointer
                        {:on-click (fn [_]
                                     (clipboard/copy-as-tsv report-data))}])
           (fu/tooltip (loc/tr :save-as-excel)
                       [:i.fal.fa-file-excel.cursor-pointer
                        {:on-click (fn [_]
                                     (excel/export-array-data view-name
                                                              report-data))}])]]

         [:div
          {:class "relative"}
          [button/square-secondary-open
           {:on-click #(swap! date-state-atom assoc :collapsed? (not collapsed?))}
           (if collapsed?
             [:i.fal.fa-cog]
             [:i.fal.fa-compress-alt])]
          [:div
           {:class (uu/cm "flex flex-col gap-3"
                          (when collapsed? "hidden h-0 p-0"))}

           (when (:date-created @date-state-atom)
             [date-picker-row date-state-atom :date-created (loc/tr :common/created-on)])
           (when (:date-passed @date-state-atom)
             [date-picker-row date-state-atom :date-passed (loc/tr :oppo/passed-on)])
           (when (:date-portfolio @date-state-atom)
             [date-picker-row date-state-atom :date-portfolio (loc/tr :oppo/moved-to-portfolio-on)])
           (when (:date-cic @date-state-atom)
             [date-picker-row date-state-atom :date-cic (loc/tr :oppo/date-CIC-presentation)])]]

         [:div
          {:ref   (fn [ref]
                    (reset! card-container-ref-state ref))
           :class "px-4 py-5"}
          view]
         (when footer-content
           [:div.card-container__footer
            footer-content])]))))

(defn ^{:jsdoc ["@nocollapse"]} format-data-point
  [_]
  (this-as js-data-point
    (let [data-point (js->clj js-data-point :keywordize-keys true)
          {:keys [y x]} data-point]
      (if (and (number? y) (not= 0 y))
        y
        ""))))

(defn default-width
  []
  (let [window-width (:width @app-db/window)
        paddings-and-margins-when-2-charts-per-row 160
        paddings-and-margins-when-single-item 210]
    (if (< window-width 768)
      (- window-width paddings-and-margins-when-single-item)
      (min (int (- (/ window-width 2) paddings-and-margins-when-2-charts-per-row))
           1100))))

(defn alj-pie-chart
  [{:keys [view-name total-deals total-amount chart-data report-data
           width date-state-atom date-range-label]}]
  (let [width (or width (default-width))
        chart-config (merge chart-data
                            {:exporting {:enabled      false
                                         :chartOptions {:title {:text (str view-name
                                                                           (when total-deals
                                                                             (str " (" total-deals ")"))
                                                                           (when total-amount
                                                                             (str " ($" total-amount "BN)")))}}}}
                            {:chart       {:type            "pie"
                                           :backgroundColor "#FFFFFF"
                                           :borderRadius    10
                                           :width           width}
                             :title       nil
                             :credits     {:enabled false}
                             :legend      {:enabled false}
                             :borderWidth 1
                             :shadow      false
                             :tooltip     {:headerFormat "<b>{point.x}</b><br/>"
                                           :pointFormat  "{point.name}: {point.y}"}
                             :plotOptions {:pie    {:colors [
                                                             "#51859A"
                                                             "#95B5AD"
                                                             "#9DA2B2"
                                                             "#80A7BF"
                                                             "#B7B2A8"
                                                             "#B8E1DC"
                                                             "#C3C8DE"
                                                             "#EDD6BD"
                                                             "#9FCDEB"
                                                             "#616163"]}
                                           :series {:dataLabels {:enabled true
                                                                 :style   {:textOutline 0
                                                                           :fontFamily  "Arial"
                                                                           :fontSize    "14px"}
                                                                 :format  "{point.name}: {point.y}"}}
                                           :column {:stacking   "normal"
                                                    :dataLabels {:enabled       true
                                                                 :style         {:textOutline 0}
                                                                 :borderWidth   0
                                                                 :verticalAlign "top"
                                                                 :formatter     format-data-point}}}})]
    [alj-card
     {:view-name        view-name
      :chart-config     chart-config
      :total-deals      total-deals
      :total-amount     total-amount
      :report-data      report-data
      :date-state-atom  date-state-atom
      :date-range-label date-range-label
      :view             [:div.reports-chart-wrapper
                         {:key (str view-name "-pie-chart-" width "-" chart-data)}
                         [chart/view
                          (chart/options chart-config)]]}]))

(defn alj-bar-chart
  [{:keys [view-name total-deals total-amount chart-data report-data footer-content width
           date-state-atom date-range-label]}]
  (let [width (or width (default-width))
        chart-config (p/deep-merge chart-data
                                   {:exporting {:enabled      false
                                                :chartOptions {:title {:text (str view-name
                                                                                  (when total-deals
                                                                                    (str " (" total-deals ")"))
                                                                                  (when total-amount
                                                                                    (str " ($" total-amount "BN)")))}}}}
                                   {:chart       {:type            "bar"
                                                  :backgroundColor "#FFFFFF"
                                                  :borderRadius    10
                                                  :width           width}
                                    :yAxis       {:type          "logarithmic"
                                                  :labels        {:enabled false}
                                                  :gridLineWidth 0
                                                  :gridLineColor "#F2F2EE"
                                                  :title         {:enabled false}}
                                    :xAxis       {:type "category"}
                                    :title       nil
                                    :credits     {:enabled false}
                                    :legend      {:enabled false}
                                    :borderWidth 1
                                    :shadow      false
                                    :tooltip     {:headerFormat "<b>{point.name}</b><br/>"
                                                  :pointFormat  "{point.name}: {point.y}"}
                                    :plotOptions {:series {:dataLabels {:enabled true
                                                                        :style   {:textOutline 0
                                                                                  :fontFamily  "Arial"
                                                                                  :fontSize    "10px"}
                                                                        :format  "{point.y}"}
                                                           :color      "#51859A"}
                                                  :column {:stacking   "normal"
                                                           :dataLabels {:enabled       true
                                                                        :style         {:textOutline 0}
                                                                        :borderWidth   0
                                                                        :verticalAlign "top"
                                                                        :formatter     format-data-point}}}})]
    [alj-card
     {:view-name        view-name
      :chart-config     chart-config
      :total-deals      total-deals
      :total-amount     total-amount
      :report-data      report-data
      :footer-content   footer-content
      :date-state-atom  date-state-atom
      :date-range-label date-range-label
      :view
      [:div.reports-chart-wrapper
       {:key (str view-name "-bar-chart-" width "-" chart-data)}
       [chart/view
        (chart/options chart-config)]]}]))



(defn vertical-bar-chart
  [{:keys [view-name total-deals total-amount chart-data report-data
           footer-content width date-state-atom]}]
  (let [width (or width (default-width))
        chart-config (p/deep-merge
                      {:exporting {:enabled      false
                                   :chartOptions {:title {:text (str view-name
                                                                     (when total-deals
                                                                       (str " (" total-deals ")"))
                                                                     (when total-amount
                                                                       (str " ($" total-amount "BN)")))}}}}
                      {:chart   {:zoomType        "xy"
                                 :backgroundColor "#FFFFFF"
                                 :borderRadius    10
                                 :width           width}
                       :yAxis   {:type          "logarithmic"
                                 :labels        {:enabled true}
                                 :gridLineWidth 0
                                 :gridLineColor "#F2F2EE"
                                 :title         {:enabled false}}
                       :title   nil
                       :credits {:enabled false}}
                      {;:legend      {:enabled false}
                       :tooltip     {:headerFormat "<b>{point.name}</b><br/>"
                                     :pointFormat  "{point.name}: {point.y}"}
                       :plotOptions {:series {:dataLabels {:enabled   true
                                                           :style     {:textOutline 0
                                                                       :fontFamily  "Arial"
                                                                       :fontSize    "10px"}
                                                           :formatter (fn []
                                                                        (this-as js-data-point
                                                                          (let [point (js->clj js-data-point
                                                                                               :keywordize-keys
                                                                                               true)
                                                                                value (:y point)]
                                                                            (str "<b>"
                                                                                 (fu/number-string->thousand-separators
                                                                                  value
                                                                                  ","
                                                                                  ".")
                                                                                 "</b>"))))}
                                              :color      "#51859A"}
                                     :column {;:stacking   "normal"
                                              :dataLabels {:enabled       true
                                                           :style         {:textOutline 0}
                                                           :borderWidth   0
                                                           :verticalAlign "bottom"
                                                           :formatter     format-data-point}}}}
                      chart-data)]
    [alj-card
     {:view-name       view-name
      :chart-config    chart-config
      :total-deals     total-deals
      :total-amount    total-amount
      :report-data     report-data
      :footer-content  footer-content
      :date-state-atom date-state-atom
      :view
      [:div.reports-chart-wrapper
       {:key (str view-name "-bar-chart-" width "-" chart-data)}
       [chart/view
        (chart/options chart-config)]]}]))


(defn alj-bar-chart-v2
  [{:keys [view-name total-deals total-amount chart-data report-data
           footer-content width date-state-atom]}]
  (let [width (or width (default-width))
        chart-config (p/deep-merge
                      {:exporting {:enabled      false
                                   :chartOptions {:title {:text (str view-name
                                                                     (when total-deals
                                                                       (str " (" total-deals ")"))
                                                                     (when total-amount
                                                                       (str " ($" total-amount "BN)")))}}}}
                      {:chart       {:type            "bar"
                                     :backgroundColor "#FFFFFF"
                                     :borderRadius    10
                                     :width           width}
                       :yAxis       {;:min           0.001
                                     :type          "logarithmic"
                                     :labels        {:enabled true}
                                     :gridLineWidth 1
                                     :gridLineColor "#F2F2EE"
                                     :title         {:enabled false}}
                       :xAxis       {:type "category"}
                       :title       nil
                       :credits     {:enabled false}
                       :legend      {:enabled false}
                       :borderWidth 1
                       :shadow      false
                       :tooltip     {:headerFormat "<b>{point.name}</b><br/>"
                                     :pointFormat  "{point.name}: {point.y}"}
                       :plotOptions {:series {:dataLabels {:enabled true
                                                           :style   {:textOutline 0
                                                                     :fontFamily  "Arial"
                                                                     :fontSize    "10px"}}
                                              :color      "#51859A"}
                                     :column {:stacking   "normal"
                                              :dataLabels {:enabled     true
                                                           :style       {:textOutline 0}
                                                           :borderWidth 0
                                                           ;:verticalAlign "top"
                                                           :formatter   format-data-point}}}}
                      chart-data)]
    [alj-card
     {:view-name       view-name
      :chart-config    chart-config
      :total-deals     total-deals
      :total-amount    total-amount
      :report-data     report-data
      :footer-content  footer-content
      :date-state-atom date-state-atom
      :view
      [:div.reports-chart-wrapper
       {:key (str view-name "-bar-chart-" width "-" chart-data)}
       [chart/view
        (chart/options chart-config)]]}]))


(defn alj-pie-chart-v2
  [{:keys [view-name total-deals total-amount chart-data report-data width
           date-state-atom date-range-label]}]
  (let [width (or width (default-width))
        chart-config (merge chart-data
                            {:exporting {:enabled      false
                                         :chartOptions {:title {:text (str view-name
                                                                           (when total-deals
                                                                             (str " (" total-deals ")"))
                                                                           (when total-amount
                                                                             (str " ($" total-amount "BN)")))}}}}
                            {:chart       {:type            "pie"
                                           :backgroundColor "#FFFFFF"
                                           :borderRadius    10
                                           :width           width}
                             :title       nil
                             :credits     {:enabled false}
                             :legend      {:enabled false}
                             :borderWidth 1
                             :shadow      false
                             :tooltip     {:headerFormat "<b>{point.x}</b><br/>"
                                           :pointFormat  "{point.name}: {point.y}"}
                             :plotOptions {:pie    {:colors [
                                                             "#51859A"
                                                             "#95B5AD"
                                                             "#9DA2B2"
                                                             "#80A7BF"
                                                             "#B7B2A8"
                                                             "#B8E1DC"
                                                             "#C3C8DE"
                                                             "#EDD6BD"
                                                             "#9FCDEB"
                                                             "#616163"]}
                                           :series {:dataLabels {:enabled   true
                                                                 :style     {:textOutline 0
                                                                             :fontFamily  "Arial"
                                                                             :fontSize    "14px"}
                                                                 :formatter (fn []
                                                                              (this-as
                                                                               js-data-point
                                                                                (let [point (js->clj js-data-point
                                                                                                     :keywordize-keys
                                                                                                     true)
                                                                                      name (aget (:point point) "name")
                                                                                      deals (:y point)
                                                                                      value (aget (:point point)
                                                                                                  "ticketSizeSum")]
                                                                                  (str "<b>"
                                                                                       name
                                                                                       "</b>"
                                                                                       "<br/>"
                                                                                       "<b>"
                                                                                       deals
                                                                                       " deals</b>"
                                                                                       "<br/>"
                                                                                       "<b>$"
                                                                                       (fu/number-string->thousand-separators
                                                                                        (fu/format "%.1f"
                                                                                                   (fu/round (/ (Math/abs value) 1) 2))
                                                                                        ","
                                                                                        ".")
                                                                                       "M</b>"))))}}
                                           :column {:stacking   "normal"
                                                    :dataLabels {:enabled       true
                                                                 :style         {:textOutline 0}
                                                                 :borderWidth   0
                                                                 :verticalAlign "top"
                                                                 :formatter     format-data-point}}}})]
    [alj-card
     {:view-name        view-name
      :chart-config     chart-config
      :total-deals      total-deals
      :total-amount     total-amount
      :report-data      report-data
      :date-state-atom  date-state-atom
      :date-range-label date-range-label
      :view             [:div.reports-chart-wrapper
                         {:key (str view-name "-pie-chart-" width "-" chart-data)}
                         [chart/view
                          (chart/options chart-config)]]}]))





(defn deals-by-business-unit
  "x-axis-type: :ticket-size-sum | :number-of-deals"
  [{:keys [deals view-name x-axis-type date-state-atom]}]
  (condp = x-axis-type
    :ticket-size-sum
    (let [oppos deals

          active-business-units (session/non-archived-named-values :oppo/group-business-units
                                                                   (alj/alj-report-pipeline-id))
          active-business-unit-ids (->> active-business-units
                                        (map :db/id)
                                        (set))
          oppos-by-business-unit (->> oppos
                                      (mapcat (fn [oppo]
                                                (->> oppo
                                                     :oppo/group-business-units
                                                     (map (fn [bu-id]
                                                            [bu-id oppo])))))
                                      (group-by first)
                                      (map (fn [[bu-id bu-id-oppos]]
                                             [bu-id
                                              (->> bu-id-oppos
                                                   (map second)
                                                   (doall))]))
                                      (into {}))

          oppos-without-active-bu (->> oppos
                                       (remove (fn [oppo]
                                                 (some active-business-unit-ids
                                                       (:oppo/group-business-units oppo)))))

          duplicate-bus (->> oppos
                             (filter (fn [oppo]
                                       (> (count (:oppo/group-business-units oppo)) 1))))


          series-data (concat
                       (->> active-business-units
                            (map (fn [nv]
                                   (let [bu-oppos (get oppos-by-business-unit (:db/id nv))]
                                     {:name       (entity/display-string nv)
                                      :y          (alj-utils/ticket-size-sum-millions bu-oppos)
                                      :dataLabels {:formatter alj-utils/million-format-with-separators}
                                      })))
                            (sort-by :y)
                            (reverse))
                       (when (seq oppos-without-active-bu)
                         [{:name       "No business unit"
                           :dataLabels {:formatter alj-utils/million-format-with-separators}
                           :y          (alj-utils/ticket-size-sum-millions oppos-without-active-bu)}]))

          chart-data {:series  [{:name       (loc/tr :oppo/tag-deal-type)
                                 :data       series-data
                                 :dataLabels {:formatter alj-utils/million-format-with-separators}}]
                      :tooltip {:headerFormat "<b>{point.name}</b><br/>"
                                :pointFormat  "{point.name}: ${point.y} M"}}
          export-data (->> chart-data
                           :series
                           (first)
                           :data
                           (map (fn [{:keys [name y]}]
                                  [name y]))
                           (sort-by first)
                           (doall))]
      [alj-bar-chart-v2
       {:view-name       view-name
        :total-deals     (count oppos)
        :total-amount    (alj-utils/ticket-size-sum-billions oppos)
        :report-data     export-data
        :chart-data      chart-data
        :date-state-atom date-state-atom}])

    :number-of-deals
    (let [oppos deals
          active-business-units (session/non-archived-named-values :oppo/group-business-units
                                                                   (alj/alj-report-pipeline-id))
          active-business-unit-ids (->> active-business-units
                                        (map :db/id)
                                        (set))
          oppos-by-business-unit (->> oppos
                                      (mapcat (fn [oppo]
                                                (->> oppo
                                                     :oppo/group-business-units
                                                     (map (fn [bu-id]
                                                            [bu-id oppo])))))
                                      (group-by first)
                                      (map (fn [[bu-id bu-id-oppos]]
                                             [bu-id
                                              (->> bu-id-oppos
                                                   (map second)
                                                   (doall))]))
                                      (into {}))

          oppos-without-active-bu (->> oppos
                                       (remove (fn [oppo]
                                                 (some active-business-unit-ids
                                                       (:oppo/group-business-units oppo)))))

          duplicate-bus (->> oppos
                             (filter (fn [oppo]
                                       (> (count (:oppo/group-business-units oppo)) 1))))

          series-data (concat
                       (->> active-business-units
                            (map (fn [nv]
                                   (let [bu-oppos (get oppos-by-business-unit (:db/id nv))]
                                     {:name (entity/display-string nv)
                                      :y    (count bu-oppos)})))
                            (sort-by :y)
                            (reverse))
                       (when (seq oppos-without-active-bu)
                         [{:name "No business unit"
                           :y    (count oppos-without-active-bu)}]))

          chart-data {:series [{:name (loc/tr :oppo/tag-deal-type)
                                :data series-data}]}
          export-data (->> chart-data
                           :series
                           (first)
                           :data
                           (map (fn [{:keys [name y]}]
                                  [name y]))
                           (sort-by first)
                           (doall))]
      [alj-bar-chart-v2
       {:view-name       view-name
        :total-deals     (count oppos)
        :total-amount    (alj-utils/ticket-size-sum-billions oppos)
        :report-data     export-data
        :chart-data      chart-data
        :date-state-atom date-state-atom}])

    [:div]))


(defn deals-by-deal-type
  [{:keys [deals view-name x-axis-type date-state-atom descending-by-y?]}]
  (condp = x-axis-type
    :ticket-size-sum
    (let [used-deal-types (->> deals
                               (mapcat :oppo/tag-deal-type)
                               (frequencies)
                               (sort-by second)
                               (reverse)
                               (keep (fn [[eid nof]]
                                       (let [entity (get @session/entities eid)]
                                         (when-not (:common/disabled? entity)
                                           entity))))
                               (doall))
          active-deal-type-ids (->> (session/non-archived-named-values :oppo/tag-deal-type (alj/alj-report-pipeline-id))
                                    (map :db/id)
                                    (set))
          deals-wo-active-dea-type? (->> deals
                                         (some (fn [oppo]
                                                 (empty? (set/difference (set (:oppo/tag-deal-type oppo)) active-deal-type-ids)))))

          show-deal-types-num 4

          shown-deal-types (take show-deal-types-num used-deal-types)

          shown-deal-type-ids (mapv :db/id shown-deal-types)

          other-deal-types (drop show-deal-types-num used-deal-types)

          other-deal-type-ids (mapv :db/id other-deal-types)

          series {:name        "ALJ Investment ($M)"
                  ;:type  "column"
                  ;:colorByPoint true

                  :color       "#51859A"
                  :dataSorting {:enabled descending-by-y?}
                  :data        (vec
                                (concat
                                 (->> shown-deal-types
                                      (map (fn [named-value]
                                             (let [named-value-label (:named-value/value named-value)
                                                   deals-w-type (->> deals
                                                                     (filterv (fn [deal]
                                                                                (some #{(:db/id named-value)}
                                                                                      (:oppo/tag-deal-type deal)))))
                                                   ticket-size-sum (alj-utils/ticket-size-sum-millions deals-w-type)]
                                               {:name       named-value-label
                                                :y          ticket-size-sum
                                                :dataLabels {:formatter alj-utils/million-format-with-separators}
                                                :drilldown  (str named-value-label)}))))
                                 (let [deals-with-other (->> deals
                                                             (remove (fn [deal]
                                                                       (let [deal-types (:oppo/tag-deal-type deal)]
                                                                         (and deal-types
                                                                              (some (:oppo/tag-deal-type deal)
                                                                                    shown-deal-type-ids))))))
                                       ticket-size-sum (alj-utils/ticket-size-sum-millions deals-with-other)]
                                   (when (seq deals-with-other)
                                     [{:name       "Other"
                                       :y          ticket-size-sum
                                       :dataLabels {:formatter alj-utils/million-format-with-separators}
                                       :drilldown  "Other"}]))))}


          categories (vec
                      (concat (->> shown-deal-types
                                   (mapv (fn [{:keys [named-value/value]}]
                                           value)))
                              ["Other"]))

          chart-data {:series  [series]
                      :xAxis   [{:categories categories}]
                      :tooltip {:headerFormat "<b>{point.name}</b><br/>"
                                :pointFormat  "{point.name}: ${point.y} M"}}

          export-data (->> chart-data
                           :series
                           (first)
                           :data
                           (map (fn [{:keys [name y]}]
                                  [name y]))
                           (sort-by first)
                           (doall))]
      [alj-bar-chart-v2
       {:view-name       view-name
        :total-deals     (count deals)
        :total-amount    (alj-utils/ticket-size-sum-billions deals)
        :report-data     export-data
        :chart-data      chart-data
        :date-state-atom date-state-atom}])

    :number-of-deals
    (let [used-deal-types (->> deals
                               (mapcat :oppo/tag-deal-type)
                               (frequencies)
                               (sort-by second)
                               (reverse)
                               (keep (fn [[eid nof]]
                                       (let [entity (get @session/entities eid)]
                                         (when-not (:common/disabled? entity)
                                           entity))))
                               (doall))
          active-deal-type-ids (->> (session/non-archived-named-values :oppo/tag-deal-type (alj/alj-report-pipeline-id))
                                    (map :db/id)
                                    (set))
          deals-wo-active-dea-type? (->> deals
                                         (some (fn [oppo]
                                                 (empty? (set/difference (set (:oppo/tag-deal-type oppo)) active-deal-type-ids)))))

          show-deal-types-num 4

          shown-deal-types (take show-deal-types-num used-deal-types)

          shown-deal-type-ids (mapv :db/id shown-deal-types)

          other-deal-types (drop show-deal-types-num used-deal-types)

          other-deal-type-ids (mapv :db/id other-deal-types)

          series {:name "# of deals"
                  ;:colorByPoint true
                  :data (vec
                         (concat
                          (->> shown-deal-types
                               (map (fn [named-value]
                                      (let [named-value-label (:named-value/value named-value)
                                            deals-w-type (->> deals
                                                              (filterv (fn [deal]
                                                                         (some #{(:db/id named-value)}
                                                                               (:oppo/tag-deal-type deal)))))]
                                        {:name      named-value-label
                                         :y         (count deals-w-type)
                                         :drilldown (str named-value-label)})))
                               (vec))
                          (let [deals-with-other (->> deals
                                                      (remove (fn [deal]
                                                                (let [deal-types (:oppo/tag-deal-type deal)]
                                                                  (and deal-types
                                                                       (some (:oppo/tag-deal-type deal)
                                                                             shown-deal-type-ids))))))]
                            (when (seq deals-with-other)
                              [{:name      "Other"
                                :y         (count deals-with-other)
                                :drilldown "Other"}]))))}

          categories (vec
                      (concat (->> shown-deal-types
                                   (mapv (fn [{:keys [named-value/value]}]
                                           value)))
                              ["Other"]))

          chart-data {:series [series]
                      :xAxis  [{:categories categories}]}

          export-data (->> chart-data
                           :series
                           (first)
                           :data
                           (map (fn [{:keys [name y]}]
                                  [name y]))
                           (sort-by first)
                           (doall))]
      [alj-bar-chart-v2
       {:view-name       view-name
        :total-deals     (count deals)
        :total-amount    (alj-utils/ticket-size-sum-billions deals)
        :report-data     export-data
        :chart-data      chart-data
        :date-state-atom date-state-atom}])

    (do
      (logging/unimplemented "no matching x axis type")
      nil)))


(defn table-with-expandable-rows
  [args]
  (let [state-atom (r/atom {:expanded-rows #{}})]
    (fn [{:keys [headers rows]}]
      (let [expanded-rows (:expanded-rows @state-atom)
            toggle-row-expanded (fn [row-index]
                                  (let [new-expanded-rows (if (some #{row-index} expanded-rows)
                                                            (set/difference expanded-rows #{row-index})
                                                            (set/union expanded-rows #{row-index}))]
                                    (swap! state-atom assoc :expanded-rows new-expanded-rows)))

            longest-label-string (some->> rows
                                          (mapcat (fn [row]
                                                    (let [{:keys [deal-status sub-rows]} row
                                                          business-units (->> sub-rows
                                                                              (mapv :business-unit))]
                                                      (concat [(loc/tr deal-status)] business-units))))
                                          (sort-by count)
                                          (last))
            longest-label-string (or longest-label-string "")
            title-cell-width (max (+ (* (count longest-label-string) 8) 60)
                                  120)]
        [:table
         [:thead
          [:tr
           (->> headers
                (map-indexed (fn [idx header]
                               ^{:key (str "header-" idx)}
                               [:th header]))
                (doall))]]
         [:tbody
          (->> rows
               (map-indexed
                (fn [row-index row ]
                  (let [{:keys [deal-status ticket-size sub-rows]} row
                        expanded? (some #{row-index} expanded-rows)]
                    ^{:key (str "expandable-row-" row-index)}
                    [:<>
                     [:tr
                      {:on-click #(toggle-row-expanded row-index)}
                      [:td
                       {:class "bg-secondary-regular"}
                       [:i.fas.fa-angle-down.mr-2
                        {:class (uu/cm "")}]
                       (loc/tr deal-status)]
                      [:td
                       (fu/format-with-separators (fu/round ticket-size 1)) "M"]]

                     (when expanded?
                       (->> sub-rows
                            (map-indexed (fn [sub-row-index sub-row]
                                           (let [{:keys [business-unit ticket-size]} sub-row]
                                             ^{:key (str "row-" row-index "-sub-row-" sub-row-index)}
                                             [:tr
                                              #_{:style    {:cursor "pointer"}
                                                 :on-click #(toggle-row-expanded row-index)}
                                              [:td
                                               {:style {:background   "#fafafa"
                                                        :padding-left "30px"
                                                        :min-width    title-cell-width}}
                                               business-unit]
                                              [:td
                                               {:style {:background "#fafafa"}}
                                               (fu/format-with-separators (fu/round ticket-size 1)) "M"]])))))]))))]
         [:tfoot
          (let [total-investment (->> rows
                                      (mapv :ticket-size)
                                      (apply +))]
            [:tr.alj-totals-row
             [:td "Grand total"]
             [:td (fu/format-with-separators (fu/round total-investment 1) 1) "M"]])
          [:tr.alj-error-row]
          [:tr.alj-duplicate-row]]]))))









